import { useMemo } from "react";

/** How far a question has been taken. */
export const PROGRESS = {
  EMPTY: "empty",
  PARTIAL: "partial",
  COMPLETE: "complete",
};

/** What the marker said about one part. */
export const OUTCOME = {
  CORRECT: "correct",
  PARTIAL: "partial",
  INCORRECT: "incorrect",
};

const outcomeOf = (status) => {
  if (!status) return null;
  const s = String(status).toUpperCase();
  if (s === "CORRECT") return OUTCOME.CORRECT;
  if (s === "PARTIALLY CORRECT") return OUTCOME.PARTIAL;
  return OUTCOME.INCORRECT;
};

/** A part only counts towards progress if it actually takes an answer. */
const takesAnAnswer = (row) => (row.total_mark || 0) > 0;

const hasAnswer = (row, answers) => {
  const draft = answers?.[row.guid];
  if (draft && (draft.text?.trim() || (draft.uploadedImages || []).length)) return true;
  const saved = row.user_question_attempt;
  return Boolean(
    saved?.user_answer_text?.trim() ||
      (saved?.user_question_attempt_attachments || []).length
  );
};

/**
 * The paper as questions rather than rows.
 *
 * A paper arrives flat: 1, 1 a), 1 b), 1 b) ii) are separate rows that only
 * share a number. The sidebar lists questions and the panel shows one whole
 * question, so both need them grouped, and both need the same answer to
 * "how far along is this one" — which is why it is computed once, here.
 *
 * Recomputed only when the rows, the drafts or the marks change, so typing
 * into an answer does not rebuild the list on every keystroke.
 */
export function useQuestionGroups({ questions = [], answers, feedbackData }) {
  return useMemo(() => {
    const byNumber = new Map();

    questions.forEach((row) => {
      if (!byNumber.has(row.number)) {
        byNumber.set(row.number, { number: row.number, parts: [] });
      }
      byNumber.get(row.number).parts.push(row);
    });

    return Array.from(byNumber.values()).map((group) => {
      const answerable = group.parts.filter(takesAnAnswer);
      const answered = answerable.filter((row) => hasAnswer(row, answers));

      const tally = { correct: 0, partial: 0, incorrect: 0 };
      // One state per part, in the order they are printed: what the rail in
      // the sidebar draws, block by block.
      const partStates = answerable.map((row) => {
        const outcome =
          outcomeOf(feedbackData?.[row.guid]?.feedbackStatus) ||
          outcomeOf(row.user_question_attempt?.feedback_correctness);
        if (outcome) {
          tally[outcome] += 1;
          return outcome;
        }
        return hasAnswer(row, answers) ? "done" : "todo";
      });
      const marked = tally.correct + tally.partial + tally.incorrect;

      let progress = PROGRESS.EMPTY;
      if (answered.length > 0) {
        progress = answered.length === answerable.length
          ? PROGRESS.COMPLETE
          : PROGRESS.PARTIAL;
      }

      // The stem carries the wording; if it has none, the first part does.
      const preview =
        group.parts.find((row) => row.question_text?.trim())?.question_text || "";

      return {
        ...group,
        answerableCount: answerable.length,
        partStates,
        answeredCount: answered.length,
        progress,
        tally,
        marked,
        isMarked: marked > 0,
        preview,
      };
    });
  }, [questions, answers, feedbackData]);
}
